<template>
  <div class="avatar-upload-modal-box">
    <UploadZone
      :current-preview="state.previewUrl"
      accept=".jpeg,.png,.jpg"
      :maxSize="3 * 1024 * 1024"
      :msg="{
        type: '请上传jpg、jpeg、png格式文件',
        size: '图片大小不能超过3MB',
      }"
      @file-select="handleFileSelect"
    >
      <template #default="{ previewUrl }">
        <div class="upload-zone-content">
          <!-- 预览图片 -->
          <div v-if="previewUrl" class="upload-zone-preview">
            <Img
              :src="previewUrl"
              :style="style"
              alt="avatar preview"
              class="upload-zone-preview__image"
              object-fit="contain"
            />
            <div class="upload-zone-preview__overlay">
              <CameraIcon class="upload-zone-preview__icon" />
              <p class="upload-zone-preview__text">{{ $t('dian-ji-geng-huan-tou-xiang') }}</p>
            </div>
          </div>

          <!-- 默认上传区域 -->
          <div v-else class="upload-zone-default">
            <CameraIcon class="upload-zone-preview__icon" />
            <p class="upload-zone-content-text">
              {{
                state.uploading
                  ? $t('shang-chuan-zhong-0')
                  : $t('dian-ji-huo-tuo-zhuai-shang-chuan-tou-xiang')
              }}
            </p>
            <p class="upload-zone-content-hint">
              {{ $t('zhi-chi-jpgpnggif-ge-shi-wen-jian-da-xiao-bu-chao-guo-2mb') }}
            </p>
          </div>
        </div>

        <!-- 上传进度 -->
        <div v-if="state.uploading" class="upload-zone-progress">
          <div class="upload-zone-progress__bar">
            <div class="upload-zone-progress__fill"></div>
          </div>
          <p class="upload-zone-progress__text">{{ $t('shang-chuan-zhong-0') }}</p>
        </div>
      </template>
    </UploadZone>

    <!-- 错误提示 -->
    <div v-if="errorMessage" class="upload-zone-error">
      <p class="upload-zone-error__text">{{ errorMessage }}</p>
    </div>

    <!-- 操作按钮 -->
    <div class="upload-zone-actions-container">
      <div class="upload-zone-actions">
        <Button variant="back" @click="modalInstance?.close()"> {{ $t('qu-xiao') }} </Button>
        <Button
          v-if="previewUrl && !state.uploading"
          class="upload-zone-btn upload-zone-btn--primary"
          @click="handleConfirmUpload"
        >
          <Loading v-if="loading" type="spinner" :size="16" />
          <div v-else class="upload-zone-btn__text">{{ $t('que-ren') }}</div>
        </Button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { message } from '@rendora/common/composables/message';
import { IFile, useUpload } from '@rendora/common/composables/useUpload';
import { postAccountUpdateInfo } from '@rendora/common/entities/user';
import { type ModalInstance } from '@rendora/common/features/modals/factory';
import UploadZone from '@rendora/common/features/upload/ui/UploadZone.vue';
import { getI18nService } from '@rendora/common/services/i18n';
import { useUserStore } from '@rendora/common/store/user';
import CameraIcon from '@rendora/design/assets/camera.svg?component';
import { Button } from '@rendora/design/components/button/index';
import { Img } from '@rendora/design/components/img/index';
import Loading from '@rendora/design/components/loading/index.vue';
import { inject, ref } from 'vue';
const { t: $t } = getI18nService();

// Props
interface Props {
  currentAvatar?: string;
  customRequest?: (path: string) => Promise<void>;
  style?: Record<string, string | number>;
}

const props = withDefaults(defineProps<Props>(), {});

const modalInstance = inject<ModalInstance>('modalInstance');

const state = ref({
  uploading: false,
  previewUrl: props.currentAvatar || '',
  errorMessage: '',
  selectedFile: null,
});

// Refs
const previewUrl = ref<string>(props.currentAvatar || '');
const errorMessage = ref<string>('');
const loading = ref<boolean>(false);
const { fetchUserInfo } = useUserStore();

const UploadService = useUpload({
  onSuccess: async (_, data) => {
    message.success($t('geng-xin-tou-xiang-cheng-gong'));
    if (data.upload_path) {
      if (props.customRequest) {
        await props.customRequest(data.upload_path);
      } else {
        await postAccountUpdateInfo({ logo_path: data.upload_path });
      }
      fetchUserInfo();
    }
    loading.value = false;
  },
  onError: () => {
    message.error($t('geng-xin-tou-xiang-shi-bai'));
    loading.value = false;
  },
});

const currentFile = ref<IFile>();

console.log(UploadService, 'UploadService', useUpload);

// Methods
const handleFileSelect = (file: File) => {
  try {
    currentFile.value = new IFile(file);
    errorMessage.value = '';
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : $t('wen-jian-xuan-ze-shi-bai');
  }
};

const handleConfirmUpload = async () => {
  if (!UploadService || !currentFile.value) {
    return modalInstance?.close();
  }
  loading.value = true;
  await UploadService.simpleUpload(currentFile.value);
  modalInstance?.close();
  loading.value = false;
};
</script>

<style lang="scss" scoped>
.avatar-upload-modal-box {
  margin: 0 auto;
  padding: 20px;
  width: 100%;
}

.upload-zone-content {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone-default {
  text-align: center;
}

.upload-zone-content-text {
  font-size: 14px;
  color: var(--x-white-1);
  margin: 0 0 8px 0;
  font-weight: 400;
}

.upload-zone-content-hint {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.upload-zone-preview {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--x-radius-medium);
  overflow: hidden;

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--x-black-6);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .upload-zone-preview__overlay {
    opacity: 1;
  }

  &__icon {
    height: 28px;
    width: 28px;
    color: var(--x-white-1);
    margin-bottom: 8px;

    img {
      width: 32px;
      height: 32px;
      filter: brightness(0) invert(1);
    }
  }

  &__text {
    color: var(--x-white-1);
    font-size: 14px;
    margin: 0;
  }
}

.upload-zone-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  padding: 12px;

  &__bar {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  &__fill {
    height: 100%;
    background: var(--x-purple-6);
    border-radius: 2px;
    animation: progress-animation 2s ease-in-out infinite;
  }

  &__text {
    color: white;
    font-size: 12px;
    margin: 0;
    text-align: center;
  }
}

@keyframes progress-animation {
  0% {
    width: 0%;
  }
  50% {
    width: 70%;
  }
  100% {
    width: 100%;
  }
}

.upload-zone-error {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: var(--x-radius-small);

  &__text {
    color: #ff4d4f;
    font-size: 12px;
    margin: 0;
  }
}

.upload-zone-actions-container {
  display: flex;
  justify-content: end;
}

.upload-zone-actions {
  margin-top: 20px;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.upload-zone-btn {
  padding: 8px 16px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--x-purple-6);
  color: white;

  &:hover {
    background: var(--x-purple-7);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__text {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
</style>
